import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
  computed,
  inject,
  ElementRef,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Overlay, OverlayRef, OverlayModule, FlexibleConnectedPositionStrategy, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ZagIcon } from '../icon/icon';
import { IconsService } from '../services/icons.service';
import type { IconCategoryGroup, IconResponse } from '@zag/core/models';

/**
 * Icon picker component for selecting an icon from the catalog.
 *
 * @example
 * ```html
 * <zag-icon-picker
 *   [placeholder]="'Select icon'"
 *   [(ngModel)]="selectedIcon"
 * />
 * ```
 */
@Component({
  selector: 'zag-icon-picker',
  templateUrl: './icon-picker.html',
  styleUrl: './icon-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ZagIcon, OverlayModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IconPicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-icon-picker',
    '[class.zag-icon-picker--open]': 'isOpen()',
    '[class.zag-icon-picker--disabled]': 'disabled()',
    '[class.zag-icon-picker--focused]': 'focused()',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class IconPicker implements ControlValueAccessor, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  protected readonly iconsService = inject(IconsService);

  @ViewChild('panelTemplate') panelTemplate!: TemplateRef<unknown>;
  @ViewChild('trigger', { read: ElementRef }) triggerElement?: ElementRef;

  /** Placeholder text when no icon is selected */
  readonly placeholder = input<string>('Select an icon');

  /** Whether the picker is disabled */
  readonly disabled = input<boolean>(false);

  /** Fallback icon shown when no explicit value is selected */
  readonly fallbackIcon = input<string | null>(null);

  /** Whether the picker is clearable */
  readonly clearable = input<boolean>(true);

  /** Internal state */
  protected readonly isOpen = signal<boolean>(false);
  protected readonly focused = signal<boolean>(false);
  protected readonly searchQuery = signal<string>('');
  private readonly selectedValue = signal<string | null>(null);

  /** Overlay reference */
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;

  /** Selected icon from the catalog */
  readonly selectedIcon = computed(() => {
    const name = this.selectedValue();
    if (!name) return null;
    return this.iconsService.findByName(name) ?? null;
  });

  /** Icon displayed in trigger (selected value or fallback icon) */
  readonly displayIcon = computed(() => {
    const selected = this.selectedIcon();
    if (selected) {
      return selected;
    }

    const fallback = this.fallbackIcon();
    if (!fallback) {
      return null;
    }

    return this.iconsService.findByName(fallback) ?? null;
  });

  /** Filtered icons based on search query */
  readonly filteredGroups = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const groups = this.iconsService.groupedIcons();

    if (!query) return groups;

    return groups
      .map((group: IconCategoryGroup) => ({
        ...group,
        icons: group.icons.filter(
          (icon: IconResponse) =>
            icon.name.toLowerCase().includes(query) ||
            icon.label.toLowerCase().includes(query) ||
            icon.tags.some((tag: string) => tag.toLowerCase().includes(query))
        ),
      }))
      .filter((group: IconCategoryGroup) => group.icons.length > 0);
  });

  /** Computed: Display text for the trigger */
  readonly displayText = computed(() => {
    const icon = this.displayIcon();
    return icon ? icon.label : this.placeholder();
  });

  /** Whether to show clear button */
  readonly showClearButton = computed(() => {
    return this.clearable() && this.selectedValue() !== null && !this.disabled();
  });

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnDestroy(): void {
    this.destroyOverlay();
  }

  writeValue(value: string | null): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // Managed via input signal
  }

  /** Toggle picker open state */
  toggle(): void {
    if (this.disabled()) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Open the picker */
  open(): void {
    if (this.disabled() || this.isOpen()) return;
    this.isOpen.set(true);
    this.focused.set(true);
    this.searchQuery.set('');
    this.createOverlay();
  }

  /** Close the picker */
  close(): void {
    this.isOpen.set(false);
    this.destroyOverlay();
  }

  /** Select an icon */
  selectIcon(icon: IconResponse): void {
    this.selectedValue.set(icon.name);
    this.onChange(icon.name);
    this.close();
  }

  /** Clear the selection */
  clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectedValue.set(null);
    this.onChange(null);
  }

  /** Handle search input */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  /** Handle focus */
  onTriggerFocus(): void {
    this.focused.set(true);
  }

  /** Handle blur */
  onTriggerBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  /** Handle keyboard events */
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (!this.isOpen()) {
        event.preventDefault();
        this.open();
      }
    }
  }

  /** Check if an icon is selected */
  isSelected(icon: IconResponse): boolean {
    return this.selectedValue() === icon.name;
  }

  private createOverlay(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.getPositionStrategy();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: 320,
      maxHeight: 400,
    });

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      if (this.elementRef.nativeElement.contains(event.target as Node)) {
        return;
      }
      this.close();
    });

    this.overlayRef.detachments().subscribe(() => {
      this.close();
    });

    this.portal = new TemplatePortal(this.panelTemplate, this.viewContainerRef);
    this.overlayRef.attach(this.portal);
  }

  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 4,
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -4,
      },
    ];

    // Use trigger element if available, otherwise fall back to host element
    // Query for trigger button directly if ViewChild isn't ready yet
    const trigger =
      this.triggerElement?.nativeElement ||
      this.elementRef.nativeElement.querySelector('.zag-icon-picker__trigger') ||
      this.elementRef.nativeElement;

    return this.overlay.position().flexibleConnectedTo(trigger).withPositions(positions).withFlexibleDimensions(false).withPush(false);
  }

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.portal = null;
  }
}
